import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser } from '../../services/authService';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  clientId: string | null;
  loading: boolean;
  error: string | null;
}

// Load initial state from localStorage
const loadInitialState = (): AuthState => {
  try {
    const savedAuth = localStorage.getItem('beesort_auth');
    if (savedAuth) {
      const { token, clientId } = JSON.parse(savedAuth);
      if (token && clientId) {
        return {
          isAuthenticated: true,
          token,
          clientId,
          loading: false,
          error: null,
        };
      }
    }
  } catch (error) {
    // If there's an error reading from localStorage, clear it
    localStorage.removeItem('beesort_auth');
  }

  return {
    isAuthenticated: false,
    token: null,
    clientId: null,
    loading: false,
    error: null,
  };
};

const initialState: AuthState = loadInitialState();

// Async thunk for login
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { clientId: string; clientSecret: string }) => {
    const response = await loginUser(credentials);
    return { token: response.accessToken, clientId: credentials.clientId };
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.clientId = null;
      state.error = null;
      localStorage.removeItem('beesort_auth');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.clientId = action.payload.clientId;
        state.error = null;
        // Save to localStorage
        localStorage.setItem('beesort_auth', JSON.stringify({
          token: action.payload.token,
          clientId: action.payload.clientId
        }));
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.clientId = null;
        state.error = action.error.message || 'Login failed';
        localStorage.removeItem('beesort_auth');
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer; 